import type { Role, PermissionRule } from "../types";
import { permissions, conditions } from "./permission-builder";

/**
 * Roles predefinidas comuns que podem ser usadas como base
 */
export const rolePresets = {
  /**
   * Super administrador - acesso total
   */
  superAdmin: (): Role => ({
    name: "super-admin",
    permissions: [
      {
        action: "*",
        resource: "*",
      },
    ],
  }),

  /**
   * Administrador - gerencia tudo exceto configurações críticas
   */
  admin: (): Role => ({
    name: "admin",
    permissions: [
      {
        action: "*",
        resource: "*",
      },
      {
        action: "*",
        resource: "system.*",
        deny: true,
      },
    ],
  }),

  /**
   * Moderador - pode moderar conteúdo
   */
  moderator: (): Role => ({
    name: "moderator",
    permissions: [
      permissions.all("posts"),
      permissions.all("comments"),
      permissions.all("users"),
      {
        action: "delete",
        resource: "users",
        deny: true, // Não pode deletar usuários
      },
      {
        action: "ban",
        resource: "users",
      },
      {
        action: "unban",
        resource: "users",
      },
    ],
  }),

  /**
   * Editor - pode criar e editar conteúdo
   */
  editor: (): Role => ({
    name: "editor",
    permissions: [
      ...permissions.crud("posts"),
      ...permissions.crud("pages"),
      ...permissions.crud("media"),
      ...permissions.readonly("users"),
      ...permissions.readonly("settings"),
    ],
  }),

  /**
   * Autor - pode gerenciar apenas seu próprio conteúdo
   */
  author: <TContext = any>(): Role<TContext> => ({
    name: "author",
    permissions: [
      {
        action: "create",
        resource: "posts",
      },
      {
        action: ["read", "update", "delete"],
        resource: "posts",
        conditions: [conditions.isOwner("authorId") as any],
      },
      ...permissions.readonly("posts"), // Pode ler todos os posts
      ...permissions.crud("media"),
      { action: "upload", resource: "media" }, // Upload é sinônimo de create para media
    ],
  }),

  /**
   * Usuário autenticado básico
   */
  user: <TContext = any>(): Role<TContext> => ({
    name: "user",
    permissions: [
      // Perfil próprio
      {
        action: ["read", "update"],
        resource: "profile",
        conditions: [conditions.isOwner() as any],
      },
      // Comentários
      {
        action: "create",
        resource: "comments",
      },
      {
        action: ["update", "delete"],
        resource: "comments",
        conditions: [conditions.isOwner() as any],
      },
      // Leitura geral
      {
        action: "read",
        resource: ["posts", "pages", "comments"],
      },
    ],
  }),

  /**
   * Visitante - acesso apenas leitura
   */
  guest: (): Role => ({
    name: "guest",
    permissions: [
      {
        action: "read",
        resource: ["posts", "pages", "comments"],
        conditions: [
          // Apenas conteúdo público
          (context: any) => context.resource?.visibility === "public",
        ],
      },
    ],
  }),
};

/**
 * Permissões predefinidas para recursos comuns
 */
export const resourcePermissions = {
  /**
   * Blog/CMS
   */
  blog: {
    posts: permissions.crud("posts"),
    pages: permissions.crud("pages"),
    comments: permissions.crud("comments"),
    media: permissions.crud("media"),
    categories: permissions.crud("categories"),
    tags: permissions.crud("tags"),
  },

  /**
   * E-commerce
   */
  ecommerce: {
    products: permissions.crud("products"),
    orders: permissions.crud("orders"),
    customers: permissions.crud("customers"),
    inventory: permissions.crud("inventory"),
    coupons: permissions.crud("coupons"),
    shipping: permissions.crud("shipping"),
  },

  /**
   * SaaS
   */
  saas: {
    organizations: permissions.crud("organizations"),
    projects: permissions.crud("projects"),
    teams: permissions.crud("teams"),
    members: permissions.crud("members"),
    billing: permissions.crud("billing"),
    subscriptions: permissions.crud("subscriptions"),
  },

  /**
   * Social
   */
  social: {
    posts: permissions.crud("posts"),
    friends: permissions.crud("friends"),
    messages: permissions.crud("messages"),
    groups: permissions.crud("groups"),
    events: permissions.crud("events"),
    notifications: permissions.crud("notifications"),
  },
};

/**
 * Helpers para criar estruturas de permissão comuns
 */
export const permissionPatterns = {
  /**
   * Padrão de propriedade - usuário só pode modificar seus próprios recursos
   */
  ownership: <
    TContext extends { user: { id: any }; resource?: { [key: string]: any } },
  >(
    resource: string,
    ownerField = "userId",
  ): PermissionRule<TContext>[] => [
    {
      action: "create",
      resource,
    },
    {
      action: ["read", "update", "delete"],
      resource,
      conditions: [conditions.isOwner(ownerField) as any],
    },
  ],

  /**
   * Padrão de colaboração - múltiplos níveis de acesso
   */
  collaboration: (resource: string): PermissionRule[] => [
    {
      action: "*",
      resource,
      conditions: [(context: any) => context.resource?.role === "owner"],
    },
    {
      action: ["read", "update"],
      resource,
      conditions: [(context: any) => context.resource?.role === "editor"],
    },
    {
      action: "read",
      resource,
      conditions: [(context: any) => context.resource?.role === "viewer"],
    },
  ],

  /**
   * Padrão de workflow - permissões baseadas em status
   */
  workflow: (
    resource: string,
    workflow: Record<string, string[]>,
  ): PermissionRule[] => {
    const rules: PermissionRule[] = [];

    Object.entries(workflow).forEach(([status, allowedActions]) => {
      allowedActions.forEach((action) => {
        rules.push({
          action,
          resource,
          conditions: [conditions.hasStatus(status)],
        });
      });
    });

    return rules;
  },
};
